/*
 * Route table. A data router (`createBrowserRouter`) with two zones:
 *
 *   - standalone auth routes (/login, /change-password) that render the
 *     AuthLayout card without the app shell, and
 *   - the authenticated shell guarded by <RequireAuth>, whose children are the
 *     feature areas. The BRAND-SCOPED feature routes nest one level deeper
 *     under <RequireBrand>, which redirects a no-active-brand principal (a
 *     super-admin with no membership selected) to the super-admin home
 *     (/brands) — so the SPA never issues a brand-scoped request without a
 *     brand (the no-brand 403 bug fix, U5):
 *       /                  queue            (U24)  ─┐
 *       /launch            launch wizard    (U6)    │ brand-scoped (operator)
 *       /runs/:id          run detail       (U24)   │ brand-scoped
 *       /runs/:id/report   inconsistency    (U25)   │ (RequireBrand)
 *       /products            product config     (U23)   │
 *       /credentials       credentials      (U23)  ─┘
 *       /team              admin: members   (U7)   brand-scoped (admin)
 *       /users             super-admin: all users (U6)
 *       /brands           super-admin: provisioning + home (U7)
 *
 * In the admin-provisioned model there is no /register or /onboarding.
 * /change-password is a voluntary password-change route (always current-
 * password-verified).
 */

import { createBrowserRouter } from "react-router-dom";
import {
  RequireAuth,
  RequireBrand,
  RequireRole,
  RequireSuperAdmin,
} from "../auth/RequireAuth";
import { Shell } from "../components/Shell";
import { LoginPage } from "./auth/LoginPage";
import { ChangePasswordPage } from "./auth/ChangePasswordPage";
import { QueuePage } from "./QueuePage";
import { LaunchWizardPage } from "./LaunchWizardPage";
import { RunDetailPage } from "./RunDetailPage";
import { RunReportPage } from "./RunReportPage";
import { ProductsPage } from "./ProductsPage";
import { HygienePage } from "./HygienePage";
import { VideoTicketsPage } from "./VideoTicketsPage";
import { CreativeAnalysisPage } from "./CreativeAnalysisPage";
import { CredentialsPage } from "./CredentialsPage";
import { TeamPage } from "./team/TeamPage";
import { UsersPage } from "./admin/users/UsersPage";
import { BrandsPage } from "./admin/brands/BrandsPage";
import { NotFoundPage } from "./NotFoundPage";

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/change-password", element: <ChangePasswordPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <Shell />,
        children: [
          // Brand-scoped feature routes: blocked (→ /brands) when no brand is
          // active, so a no-membership super-admin never triggers a 403.
          {
            element: <RequireBrand />,
            children: [
              { index: true, element: <QueuePage /> },
              {
                path: "launch",
                element: (
                  <RequireRole capability="operate">
                    <LaunchWizardPage />
                  </RequireRole>
                ),
              },
              { path: "runs/:id", element: <RunDetailPage /> },
              { path: "runs/:id/report", element: <RunReportPage /> },
              { path: "products", element: <ProductsPage /> },
              { path: "hygiene", element: <HygienePage /> },
              { path: "video-tickets", element: <VideoTicketsPage /> },
              // The Creative Analysis tab (plan 2026-07-22-001 U7) replaced
              // the Performance page UI — same /performance path, so
              // bookmarks live on.
              { path: "performance", element: <CreativeAnalysisPage /> },
              {
                path: "credentials",
                element: (
                  <RequireSuperAdmin>
                    <CredentialsPage />
                  </RequireSuperAdmin>
                ),
              },
              { path: "team", element: <TeamPage /> },
            ],
          },
          // Super-admin surfaces: reachable without an active brand (this is the
          // valid place a no-membership super-admin lands).
          { path: "users", element: <UsersPage /> },
          { path: "brands", element: <BrandsPage /> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
